/**
 * Act 1, beat 7 — ***Out***. The last beat of the act.
 *
 * Spec §7.7 reads, in full: *"Hand-framed static shot. Wet grass. The palette
 * blooms into full colour for the first time in the game. Hold it long. She does
 * not celebrate; she looks back."* That is a shot, not a beat — so this one also
 * takes the back half of §7.6, the part beat 6 could not carry: *"rolling escape
 * under the trailer, through the rotten boards, dogs on the scent, the fence,
 * the ditch."* See `docs/story-review.md` S-3.
 *
 * Beat 7 is: **the dogs, the boards, the roll, the ditch — and then the colour.**
 *
 * ── The one sentence this beat has to deliver ────────────────────────────────
 *
 * **She gets out by doing the only joyful thing the game has, at the one moment
 * it is not a choice, and the world is green on the other side of it.**
 *
 * The roll has been available since beat 3 and has cost her nothing since. Here
 * the verb is finally load-bearing: the boards do not open for a walk, the fall
 * of the ground is the only speed she has, and the ditch is at the bottom of it.
 * Spec §5.4 asks for the roll to feel like the only joy in Act 1 and then for the
 * game to ask her to risk it. **This is the asking.**
 *
 * And then the game stops asking anything at all, for about ninety seconds, in
 * colour.
 *
 * ── Rain washes her trail everywhere except where she hid ────────────────────
 *
 * The beat's one mechanical idea, and it inverts three beats of teaching in a
 * sentence. Beat 4 established that water removes scent samples; there it was a
 * puddle she stood in. Here it is the sky, and it is everywhere — so the dogs
 * cannot track her across open ground at all. What survives is every place she
 * was **under cover**, because the rain does not reach there.
 *
 * **Hiding leaves evidence.** They do not follow her. They walk from one place
 * she felt safe to the next, and they are always right.
 *
 * The rule is taught **inside this beat, on trails laid inside this beat** —
 * first on the men's ground along the shed wall in stage 1, where the player
 * watches the mechanic happen to somebody else, and only then on hers. It cannot
 * be a callback to beat 6: beat 6 does not run the scent system, D-079's
 * hand-over is a **page reload** which destroys every runtime object including a
 * `ScentTrail`, and `SCENT_TUNING.lifetimeSeconds` is 90 s anyway. An earlier
 * draft of the plan sent stage 1's dogs to "the pallet stack she hid behind two
 * minutes ago" and it was impossible three separate ways. Teaching it here on
 * somebody else's trail is also better staging, because the player can see the
 * cause.
 *
 * ── Every stage ends on a clock as well as on its condition ──────────────────
 *
 * D-097, and stage 3 is where the first draft of `docs/beat-7-plan.md` got it
 * wrong. The draft said stage 3 "ends on geometry she cannot avoid". It does not:
 * the fall is open ground, and a player who stops halfway down it, or who never
 * enters the roll at all, is standing on a hillside with two dogs and no
 * terminating condition. *"She cannot avoid the ditch"* is true of a body that
 * keeps moving and false of one that has stopped, and the whole D-097 lesson is
 * that those are different players.
 *
 * So every stage below carries a backstop, and `out-beat.spec.ts` plays the beat
 * with no input at all and reaches `end`. **That spec was written before stage 2
 * existed**, which is the other half of the same rule.
 *
 * ── The backstops are the world getting harder, not the game taking over ─────
 *
 * Spec §5.6's thesis is that there is no game over: the world gets harder and she
 * keeps going. This beat is the one place in Act 1 where the anti-softlock
 * machinery can *be* that instead of apologising for it.
 *
 * Stage 2's backstop is **capture** — the dogs' approach tightens until they have
 * her, and she comes back under the trailer with the run reset. Stage 3's is the
 * dogs closing and **driving her into the ditch**, which is the one thing dogs
 * do and which puts her exactly where the beat needs her. Neither is a fade that
 * does it for her.
 *
 * **Capture exists in stage 2 only.** Stage 3's backstop is deliberately not
 * capture: once she is rolling she is out, because a roll run the player can lose
 * is a roll run the player replays, and this one is meant to be remembered rather
 * than practised.
 *
 * ── Nothing here touches the world ───────────────────────────────────────────
 *
 * Stages emit **cues**; `src/world/outDirector.ts` is the only file that knows
 * any of this is triangles, dogs, water or a palette. Same rule as every beat
 * since `born.ts`: if a stage in this file ever imports Three, the beat has
 * stopped being replayable in Node, and the mistake will be here rather than in
 * the sequencer. The spatial stages wait on flags the *room* raises, for the
 * reason `otto.ts` gives — "she is in the water" is a fact about a room.
 */

import type { Beat, Stage, StoryContext } from '@/story/types';

/**
 * Interactables. Both of them fail, and that is the content.
 *
 * Spec §5.4 says the roll *"smashes through rotten boards"*, and §7 forbids text.
 * So the teaching is the geometry: one bay of a hundred metres of steel fence has
 * been patched with timber, the timber is visibly wrong, and the player has
 * already walked past it once — beat 6 built that asset and placed it whole.
 *
 * `nudge` and `root` are offered on it and **must be allowed to do nothing.**
 * D-043 says a verb the beat has not offered must not be reachable; the corollary
 * is that a verb the beat HAS offered must be allowed to fail honestly. A prompt
 * would be the other way to teach this and it is the wrong way — what breaks the
 * boards is arriving at them fast, and the only way to be fast is to roll.
 *
 * If a stopwatch says the speed threshold is unreachable for an ordinary player,
 * **lower the threshold** (`BOARDS_BREAK_SPEED` in `src/world/hedgerow.ts`). Do
 * not add a prompt.
 */
export const OUT_IDS = {
  /** The timber bay, offering `nudge` and `root`. Neither completes anything. */
  boards: 'out.boards',
} as const;

/** Story flags the GAME raises. All of them are things only the simulation knows. */
export const OUT_FLAGS = {
  /**
   * She is out from under the trailer's deck.
   *
   * Raised by the room when she is clear of `TRAILER_SHADOW_REGION`, not when the
   * player first presses a key. D-090's rule for the fifth time: a flag describes
   * what the world has finished doing, never what it has begun.
   */
  leftTheTrailer: 'out.leftTheTrailer',
  /**
   * The timber has gone. **Not the same thing as being through it** — see
   * `pastTheFence`, and see `BOARDS_PATIENCE` for why that distinction is what
   * keeps the no-input run finite.
   */
  boardsBroken: 'out.boardsBroken',
  /**
   * She has pushed or rooted the intact timber, and it shuddered and held.
   * Read by NO stage — only by her wayfinding goal (`src/game/wayGoals.ts`),
   * which after a failed try turns her attention back up the run-up (D-209).
   */
  boardsTried: 'out.boardsTried',
  /** She is on the field side of the fence line. */
  pastTheFence: 'out.pastTheFence',
  /** She is in the ditch's water. */
  inTheWater: 'out.inTheWater',
  /** She is through the gap in the hedge and standing in the field. */
  throughTheHedge: 'out.throughTheHedge',
  /**
   * She has set off — for the road, or back for the hedge.
   *
   * Either counts, and that is rule 4: the field has no objective, so the only
   * thing the beat can honestly wait for is the player deciding they are done
   * with it. Going *back* is as valid as going on, and it is the more interesting
   * of the two.
   */
  headingOut: 'out.headingOut',
} as const;

/** Every cue this beat can emit. The director switches on these and nothing else. */
export const OUT_CUES = {
  /**
   * Under eight metres of steel: rain on the deck above her, then boots on wet
   * concrete, then two sets of claws.
   *
   * The beat opens exactly where beat 6 closed, which is the only hand-over in
   * the game that does not need to re-establish anything.
   */
  open: 'out.open',
  /**
   * The dogs work the **sheltered ground along the shed wall**, where the men
   * have been, and put their noses down there.
   *
   * Rule 1, demonstrated on somebody else's trail before it is ever used on hers.
   * **They do not come to her.** The whole point of stage 1 is that the player
   * watches two dogs be *correct* about a place, and works out what that will
   * mean in about forty seconds' time.
   */
  dogsWorkTheWall: 'out.dogsWorkTheWall',
  /**
   * `nudge` and `root` become available on the timber. Both fail.
   *
   * Offered rather than required, and offered precisely so they can be tried —
   * a player who never touches the boards learns the same lesson from the fence
   * simply not opening, one who tries learns it faster.
   */
  offerBoards: 'out.offerBoards',
  /**
   * Capture goes live, and it is live **here and nowhere else in the beat.**
   */
  armCapture: 'out.armCapture',
  /**
   * She went through. One hit, and it is the loudest thing in Act 1.
   */
  boardsGo: 'out.boardsGo',
  /**
   * Eighteen metres of wet grass at 4 %, and the roll's own momentum.
   *
   * **No camera cut, no slow motion, no scripted anything.** Chase framing (§4:
   * the rig pulls back and low during rolls) and nothing else. The single
   * quickest way to ruin the one moment this game has been building its best verb
   * toward is to take the camera away from the player during it.
   */
  theRun: 'out.theRun',
  /** The stage 3 backstop: the dogs close, and she goes in whether she meant to. */
  driveHerIn: 'out.driveHerIn',
  /**
   * Water. The dogs stop at the bank, cast about, and do not cross.
   *
   * **The wear steps here** (D-002, `docs/story-review.md` S-4) and it is the
   * biggest single step in the game, because what she wades out of is a farm
   * drain. She goes into the water at one value and comes out at another, in a
   * beat that then holds a camera on her for ninety seconds. That is the whole
   * reason the channel exists.
   */
  theDitch: 'out.theDitch',
  /**
   * The authored shot. Hand-framed, static, **the camera does not follow her.**
   * And the bloom starts: seven seconds, eased, chroma last.
   *
   * The bloom does not start when she leaves the yard. She comes out of the ditch
   * into a hedgerow and the hedge is still the yard's palette; it starts here, on
   * the cut, because a bloom that arrives at the end of an exertion is the only
   * kind that is earned.
   */
  theCut: 'out.theCut',
  /**
   * Control returns, and the field has **no objective, no timer, no watcher, no
   * interactable and no prompt.**
   *
   * Spec §7.7 says *hold it long*; `docs/story-review.md` S-7 adds the half the
   * spec left out — the game has never once let the player enjoy the roll without
   * a reason. The consequence-free straw pen (D-069, beat 3's `hers`) is the only
   * other place, and it is thirty seconds long in a room with a ceiling.
   *
   * **Do not put a collectible in it. Do not put a glyph in it. Do not gate the
   * exit on anything.** The single most likely way to ruin this beat is to give
   * the player something to do.
   */
  theField: 'out.theField',
  /**
   * The camera takes over one last time. She stops, and turns, and the shed is
   * small and orange in the middle distance under a sky that is now enormous.
   *
   * Spec §7.7's own line — *she does not celebrate; she looks back* — delivered
   * by the game rather than by the player, which is the honest way round. The
   * stage still ends, because the game is not branching and Act 2 happens either
   * way, but **it ends on her and not on them.**
   */
  sheLooksBack: 'out.sheLooksBack',
  /** Fade to black. The end of Act 1. */
  fadeOut: 'out.fadeOut',
  /** The beat is over — and the trail, the dogs and the bloom stop being ticked. */
  end: 'out.end',
} as const;

/**
 * How long the dogs get before the beat stops waiting for the player to look.
 *
 * Twelve seconds is short for a teach, and it is short on purpose: this is the
 * *first* statement of rule 1 and not the whole lesson. It is repeated, on her own
 * trail, for the entire length of stage 2 — where the player is trying to do
 * something else and can watch it out of the corner of an eye, which is where a
 * mechanic actually gets learnt.
 *
 * A player who leaves the trailer early ends the stage early and loses nothing;
 * the dogs go on working the wall regardless.
 */
const DOGS_PATIENCE = 12;

/**
 * The backstop of the backstop, and it is the number that keeps the no-input run
 * finite.
 *
 * Stage 2's *designed* failure path is capture: at `CAPTURE_AFTER` (75 s, in the
 * director) the dogs' approach tightens until they have her, and she comes back
 * under the trailer with the run reset. **After the second capture the boards are
 * already broken** — a worker came through the fence and did not fix it properly
 * — which is spec §5.6's thesis wearing a diegetic coat: the world got harder, and
 * then it got easier, and neither was a menu.
 *
 * But `boardsBroken` is **not** `pastTheFence`, and the difference is exactly the
 * D-097 defect. Broken boards are an opening; a player who presses nothing still
 * does not walk the eight metres through it. So the stage waits on being THROUGH
 * the fence, and this clock is what ends it for somebody who never will be:
 *
 *     75  first capture
 *   + 75  second capture, after which the timber is gone
 *   + 15  grace to walk through a bay that is now open
 *   = 165
 *
 * On expiry `exit` emits `boardsGo` and the director puts her through, the same
 * shape beat 6's `putHerUnder` has. **This is the longest stage in the game** and
 * it makes the no-input run about six and a half minutes; `out-beat.spec.ts` says
 * so and sets its timeout accordingly.
 */
const BOARDS_PATIENCE = 165;

/**
 * How long the run may take before the dogs finish it.
 *
 * Sixty seconds, and **it cannot be reached by anybody playing** — eighteen metres
 * downhill takes about six. It is here for the player who stopped, which the first
 * draft of the plan did not believe existed. See the header.
 */
const RUN_PATIENCE = 60;

/**
 * Water, and two dogs deciding not to come in — and then the hedge gap.
 *
 * Eighteen, from ten (D-209). The gap is nine metres west of where the run
 * lands, and at ten the clock cut to the bloom before her first wayfinding
 * look had finished (`minimumPatience()`, held by `wayfinding.spec.ts`): the
 * stage ended around a player who had not been told where the gap was.
 */
const DITCH_HOLD = 18;

/**
 * The held shot.
 *
 * Sixteen seconds against a seven-second bloom, so the frame is held for nine
 * seconds *after* the colour has finished arriving. That ratio is the shot: a
 * bloom the camera cuts away from the moment it lands is a transition, and this
 * is not a transition.
 */
const OUT_HOLD = 16;

/**
 * Ninety seconds of no objective, plus thirty of nobody hurrying.
 *
 * Rule 4, and it is content rather than padding. **This is the first named item on
 * the cut list** if a session collapses (`docs/beat-7-plan.md`): 120 → 45 costs
 * the beat's generosity and not its meaning. Nothing else in the field may be cut
 * before it.
 */
const FIELD_PATIENCE = 120;

/** She stops, she turns, and the act is over. */
const LOOK_BACK_HOLD = 9;

/**
 * The fade needs frames to happen in.
 *
 * An eighth stage where the plan's table has seven, for the same reason beat 5
 * and beat 6 each have one: a `fadeOut` emitted from the *exit* of the stage
 * before it has no frames left to run in, and the beat hands over to black
 * without ever having faded.
 */
const CLOSE_HOLD = 4;
/** The act break's black, after `end` and before the hand-over. See the stage. */
const BLACK_HOLD = 2;

/** A stage that is purely a length of time with cues at the front of it. */
function hold(id: string, seconds: number, ...cues: readonly string[]): Stage {
  return {
    id,
    enter: (_ctx, emit): void => {
      for (const cue of cues) emit(cue);
    },
    done: (ctx: StoryContext): boolean => ctx.stageTime >= seconds,
  };
}

/** A stage that waits for a flag, or for the player to have had long enough. */
function waitFor(id: string, flag: string, patience: number, ...cues: readonly string[]): Stage {
  return {
    id,
    enter: (_ctx, emit): void => {
      for (const cue of cues) emit(cue);
    },
    done: (ctx: StoryContext): boolean => ctx.completed.has(flag) || ctx.stageTime >= patience,
  };
}

export const OUT_BEAT: Beat = {
  number: 7,
  id: 'act1.out',
  stages: [
    // ── the dogs ─────────────────────────────────────────────────────────────
    // From under the trailer: boots, then two sets of claws on wet concrete.
    // The handler is at the shed door.
    //
    // **The dogs do not come to her.** They work the sheltered strip along the
    // shed wall, where the men have been all evening, and put their noses down
    // there. Rule 1, on somebody else's trail, before it is ever used on hers.
    waitFor('the-dogs', OUT_FLAGS.leftTheTrailer, DOGS_PATIENCE, OUT_CUES.open, OUT_CUES.dogsWorkTheWall),

    // ── the boards ───────────────────────────────────────────────────────────
    // Out from under, across the last of the yard, to the fence. `nudge` and
    // `root` are offered on the timber and both fail. The dogs are working the
    // cover behind her, correctly and slowly, and every place she shelters on the
    // way is a place they will be right about.
    //
    // She cannot push it and she cannot root it. What breaks it is arriving above
    // a speed threshold, and the only way to reach that speed is to roll the
    // eight metres from under the trailer. **The yard is flat there** — it falls
    // west toward its drain, not north toward the fence — so this is the roll's
    // own acceleration and nothing else. The fall on the far side is the roll's
    // *reward*, not its enabler, and confusing the two is how the threshold ends
    // up unreachable.
    {
      ...waitFor(
        'the-boards',
        OUT_FLAGS.pastTheFence,
        BOARDS_PATIENCE,
        OUT_CUES.offerBoards,
        OUT_CUES.armCapture,
      ),
      exit: (ctx, emit): void => {
        if (!ctx.completed.has(OUT_FLAGS.pastTheFence)) emit(OUT_CUES.boardsGo);
      },
    },

    // ── the run ──────────────────────────────────────────────────────────────
    // Eighteen metres of wet grass at 4 %, with the roll's own momentum and two
    // dogs that do not need to be fast.
    //
    // This is the beat, and the way to get it wrong is to help. No cut, no slow
    // motion, no scripted line, no camera that knows where the ditch is.
    {
      ...waitFor('the-run', OUT_FLAGS.inTheWater, RUN_PATIENCE, OUT_CUES.theRun),
      exit: (ctx, emit): void => {
        if (!ctx.completed.has(OUT_FLAGS.inTheWater)) emit(OUT_CUES.driveHerIn);
      },
    },

    // ── the ditch ────────────────────────────────────────────────────────────
    // Water. The dogs stop at the bank. The trail ends mid-field and they cast
    // about and do not cross. **The wear steps here.** She wades east under the
    // hedge, toward the light.
    // **`waitFor`, not `hold`, and the first build was a `hold`.** `throughTheHedge`
    // was raised by the room and read by NOTHING — the same defect class as
    // `CULL_RAIL.y`, `CullDirector.ended` and `cast.wet`: a value computed with
    // care that nothing consumes. Found on independent review.
    //
    // A pure ten-second timer here also bets on a world it cannot see: it advances
    // into the authored cut whether or not she has actually waded anywhere, so a
    // player who stops in the water arrives at the bloom having not made the move
    // the stage exists for. Ending on the flag *or* the clock is the shape every
    // other stage in this beat already has, and it costs one word.
    waitFor('the-ditch', OUT_FLAGS.throughTheHedge, DITCH_HOLD, OUT_CUES.theDitch),

    // ── out ──────────────────────────────────────────────────────────────────
    // The authored shot, and the bloom. Hand-framed, static; she comes up out of
    // the hedge into the field and the camera lets her.
    //
    // Strings enter here in the shipped game (spec §8) and they are the beat's
    // named cut line — see `docs/beat-7-plan.md`. `field-air` alone is honest;
    // a rushed string bed under the game's best shot is not.
    hold('out', OUT_HOLD, OUT_CUES.theCut),

    // ── the field ────────────────────────────────────────────────────────────
    // Control returns. Rule 4. Nothing to do, nowhere to be, nobody watching.
    waitFor('the-field', OUT_FLAGS.headingOut, FIELD_PATIENCE, OUT_CUES.theField),

    // ── she looks back ───────────────────────────────────────────────────────
    // The camera takes over one last time.
    hold('she-looks-back', LOOK_BACK_HOLD, OUT_CUES.sheLooksBack),

    // ── close ────────────────────────────────────────────────────────────────
    {
      ...hold('close', CLOSE_HOLD, OUT_CUES.fadeOut),
      exit: (_ctx, emit): void => emit(OUT_CUES.end),
    },

    // ── black ────────────────────────────────────────────────────────────────
    // Two seconds on black before the hand-over to beat 8's card. They were
    // always there, by accident: the host's chain wait never read beat 7's
    // fade, so it fell through to `CHAIN_MAX_WAIT` (2 s) after `end`. As a part
    // module the fade IS read, and it is past 0.99 before `close` ends — so the
    // act break would have lost them. Authored here instead (session 78).
    hold('black', BLACK_HOLD),
  ],
};

/**
 * The no-input length of this beat, seconds, so the gate does not have to
 * re-derive it and drift from the constants above.
 *
 * `out-beat.spec.ts` asserts against this rather than against a literal, because
 * the one thing worse than a slow test is a slow test whose timeout was tuned to
 * a number nobody can find any more.
 */
export const OUT_NO_INPUT_SECONDS =
  DOGS_PATIENCE + BOARDS_PATIENCE + RUN_PATIENCE + DITCH_HOLD + OUT_HOLD + FIELD_PATIENCE + LOOK_BACK_HOLD + CLOSE_HOLD + BLACK_HOLD;
